/**
 * `ParseStatusIcon` — renders the parse-status of a test file as a small
 * colored icon (green ✓, spinner, red ✗, or a "pending" pill), with the
 * status text on hover.
 *
 * Shared by the profile editor's file picker (icon-only for compactness)
 * and the template sandbox's file list (currently uses its own inline
 * `parseStatusBadge`; can migrate to `withLabel` if that visual parity
 * matters). Extracted so future file lists don't diverge.
 */
import { ArrowPathIcon, CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";

import type { TestFile } from "@api/rest/prompt-sandbox";

interface Props {
  status: TestFile["parse_status"];
  /** When true, renders the icon + human-readable label instead of icon only. */
  withLabel?: boolean;
}

export function ParseStatusIcon({ status, withLabel = false }: Props) {
  if (status === "complete") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-700" title="Parsed" aria-label="Parsed">
        <CheckCircleIcon className="size-4" />
        {withLabel && "Parsed"}
      </span>
    );
  }
  if (status === "parsing") {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-amber-600" title="Parsing…" aria-label="Parsing">
        <ArrowPathIcon className="size-4 animate-spin" />
        {withLabel && "Parsing…"}
      </span>
    );
  }
  if (status === "failed") {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-text-error-primary"
        title="Parse failed"
        aria-label="Parse failed"
      >
        <XCircleIcon className="size-4" />
        {withLabel && "Failed"}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-xs text-text-tertiary italic"
      title="Pending"
      aria-label="Pending"
    >
      Pending
    </span>
  );
}
